// @file Padlet attachment shape for Tldraw
// To render the Padlet attachment shape in Tldraw. We use a dynamic Vue component to render the Padlet attachment.
import { FallbackAttachmentType, fallbackImageUrl } from '@@/bits/attachments'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { setupVueApp } from '@@/bits/vue'
import { updatePollShapeSize } from '@@/bits/whiteboard'
import { FRAME_PREVIEW_IMAGE_WIDTH } from '@@/bits/whiteboard_preview_utils'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardAttachmentsStore } from '@@/pinia/whiteboard_attachments'
import WhiteboardAttachment from '@@/vuecomponents/WhiteboardAttachment.vue'
import type { Link } from '@padlet/arvo'
import { LinkHelpers } from '@padlet/beethoven-client'
import { processedUrl } from '@padlet/vivaldi-client'
import type { PadletAttachmentShape, PadletAttachmentShapeProps } from '@padlet/whiteboard-schema'
import { padletAttachmentShapeMigrations, padletAttachmentShapeProps } from '@padlet/whiteboard-schema'
import { omit } from 'es-toolkit'
import type { ReactElement } from 'react'
import { useEffect, useMemo } from 'react'
import type { Geometry2d, RecordProps, SvgExportContext, TLResizeInfo, TLShapeId, TLShapePartial } from 'tldraw'
import { HTMLContainer, Rectangle2d, resizeBox, ShapeUtil, toDomPrecision, useQuickReactor } from 'tldraw'
import 'tldraw-root/tldraw.css'

function getVueContainerId(props: PadletAttachmentShapeProps): string {
  return `tldraw_vue_padlet_attachment_shape_for_post_${props.postId}`
}
function getFallbackAttachmentType(link: Link): FallbackAttachmentType {
  if (link == null) return FallbackAttachmentType.BrokenLink
  if (LinkHelpers.isAudio(link)) return FallbackAttachmentType.Audio
  if (LinkHelpers.isVideo(link)) return FallbackAttachmentType.Video
  if (LinkHelpers.isPhoto(link)) return FallbackAttachmentType.Image
  if (LinkHelpers.isFile(link)) return FallbackAttachmentType.File
  if (LinkHelpers.isExcelSpreadsheet(link)) return FallbackAttachmentType.Spreadsheet
  if (LinkHelpers.isPowerpointPresentation(link)) return FallbackAttachmentType.Presentation
  if (LinkHelpers.isGoogleDoc(link)) return FallbackAttachmentType.GoogleDocument
  if (LinkHelpers.isDocument(link)) return FallbackAttachmentType.TextDocument
  if (LinkHelpers.isPage(link)) return FallbackAttachmentType.Link
  return FallbackAttachmentType.Link
}

/**
 * Get a fake poll SVG to show in the frame browser when the shape is a poll
 * @param shape
 * @returns Poll SVG
 */
function getPollSvg(shape: PadletAttachmentShape): ReactElement {
  return (
    <svg
      width={shape.props.w}
      height={shape.props.h}
      viewBox={`0 0 ${shape.props.w} ${shape.props.h}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0" y="0" width="100%" height="100%" fill="#1E1E1E" rx="16" ry="16" />
      <text x="20" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
        Poll
      </text>

      <g className="poll-choice" transform="translate(10, 40)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 1
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-choice" transform="translate(10, 80)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 2
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-choice" transform="translate(10, 120)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 3
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-choice" transform="translate(10, 160)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 4
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-footer" transform={`translate(0, ${shape.props.h - 45})`}>
        <rect x="0" y="0" width={shape.props.w} height="40" fill="#1E1E1E" rx="16" ry="16" />
        <text x="20" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Votes • Show Results
        </text>
      </g>
    </svg>
  )
}

export class PadletAttachmentShapeUtil extends ShapeUtil<PadletAttachmentShape> {
  static override type = 'padlet-attachment-shape' as const
  static override props: RecordProps<PadletAttachmentShape> = padletAttachmentShapeProps

  static override migrations = padletAttachmentShapeMigrations

  getDefaultProps(): PadletAttachmentShapeProps {
    return {
      w: 200,
      h: 200,
      zoom: 1,
      resizable: true,
      postId: 0,
      isPoll: false,
    }
  }

  override canBind = (): boolean => true
  override canEdit = (): boolean => false
  override canResize = (): boolean => true
  override isAspectRatioLocked = (_shape: PadletAttachmentShape): boolean => true

  vueApp: Record<TLShapeId, Vue> = {}
  aspectRatios: Record<TLShapeId, number> = {}

  async toSvg(shape: PadletAttachmentShape, ctx: SvgExportContext): Promise<null | ReactElement> {
    if (shape.props.isPoll) {
      return getPollSvg(shape)
    }
    const post = useSurfacePostsStore().postEntitiesById[shape.props.postId]
    if (post == null) return null
    const url = post.wish_content?.attachment_props?.url ?? post.attachment
    const linkAttributes =
      useSurfaceAttachmentsStore().linkAttributesByUrl[url] ??
      (await useSurfaceAttachmentsStore().fetchAndStoreLinkAttributes(url))
    if (linkAttributes == null) return <rect width={shape.props.w} height={shape.props.h} fill="#1d1d1d" rx="12" />
    let currentImageUrl = linkAttributes?.preview_image?.url
    // we have to process the image so that we don't load the huge image,
    // for now we resize it to the size of the card preview, which is more than enough for rendering on the card preview
    currentImageUrl =
      currentImageUrl != null ? processedUrl(currentImageUrl, { width: FRAME_PREVIEW_IMAGE_WIDTH }) : undefined
    currentImageUrl ??= fallbackImageUrl(getFallbackAttachmentType(linkAttributes), 'dark', null)
    if (currentImageUrl == null) {
      return <rect width={shape.props.w} height={shape.props.h} fill="#1d1d1d" rx="12" />
    }
    return (
      <image
        width={shape.props.w}
        height={shape.props.h}
        href={currentImageUrl}
        preserveAspectRatio="xMidYMin slice"
        clip-path="inset(0% round 12px)"
      />
    )
  }

  getGeometry(shape: PadletAttachmentShape): Geometry2d {
    // Take zoom into account when calculating the geometry.
    // This allows the hover outline and the selection outline to
    // match the actual size of the shape.
    const geometry = new Rectangle2d({
      width: shape.props.w * shape.props.zoom,
      height: shape.props.h * shape.props.zoom,
      isFilled: true,
    })
    return geometry
  }

  // The return type of this is from tldraw's ShapeUtil.onResize, it doesn't do us any good to try and match that so leave it as any for now
  override onResize: any = (shape: PadletAttachmentShape, info: TLResizeInfo<PadletAttachmentShape>) => {
    if (!shape.props.resizable) return shape

    // #region Instead of resizing the shape's width and height, we update the zoom level
    // for these attachment types:
    // - Audio player v2
    // - Polls
    const isAudioPlayerV2 = isAppUsing('audioPlayerV2Sandbox') && shape.meta.audioPlayerVersion === 2
    if (isAudioPlayerV2 || shape.props.isPoll) {
      const MIN_ZOOM = 1
      const MAX_ZOOM = 2.75
      const currentZoom = shape.props.zoom
      const newZoom = Math.min(Math.max(currentZoom * info.scaleX, MIN_ZOOM), MAX_ZOOM)
      return {
        ...shape,
        props: {
          ...shape.props,
          zoom: newZoom,
        },
      }
    }
    // #endregion

    return resizeBox(shape, info)
  }

  override onResizeEnd(
    _initial: PadletAttachmentShape,
    current: PadletAttachmentShape,
  ): TLShapePartial<PadletAttachmentShape> {
    if (!current.props.isPoll) return current

    // Correct the poll size after resizing. This is because we've just updated the display mode
    // from `default` to `slideshow` which is slightly bigger. The user can resize their polls
    // to correct the size.
    void updatePollShapeSize(current.id)

    // We update the size asynchronously, so don't make any change to the shape now.
    return current
  }

  override onBeforeUpdate(prev: PadletAttachmentShape, next: PadletAttachmentShape): PadletAttachmentShape {
    const isPostIdReset = prev.props.postId !== 0 && next.props.postId === 0

    if (isPostIdReset) {
      // When an attachment is being uploaded, we temporarily set postId to 0 until upload completes.
      // However, this causes issues with resizing because:
      // 1. The onResize handler receives the cached shape with postId=0 even after upload finishes
      // 2. This prevents the attachment from displaying properly after upload
      // 3. postId should never be 0 since it's a required field
      // We restore the previous non-zero postId to fix these issues and
      // delete the meta keys to mark the shape as success upload and to prevent deleteFailedUploads from being executed
      return {
        ...next,
        props: {
          ...next.props,
          postId: prev.props.postId,
        },
        meta: omit(next.meta, ['isUploaded', 'uploadKey', 'tabId']),
      }
    }
    return next
  }

  // Initial setup and mounting of the Vue component
  component(shape: PadletAttachmentShape): JSX.Element {
    const editorZoom = this.editor.getZoomLevel()
    const xAudioPlayerV2 = isAppUsing('audioPlayerV2Sandbox') && shape.meta.audioPlayerVersion === 2

    const createVueApp = async (): Promise<void> => {
      const width = shape.props.w
      const height = shape.props.h
      const app = await setupVueApp({
        el: `#${getVueContainerId(shape.props)}`,
        usePinia: false,
        disableAccessibilityChecks: true,
        rootComponent: WhiteboardAttachment,
        otherVueOptions: {
          parent: (window as any).app,
          data: function () {
            return {
              postId: shape.props.postId,
              uploadKey: shape.meta.uploadKey,
              width,
              height,
              zoom: editorZoom,
              xAudioPlayerV2,
              shapeId: shape.id,
            }
          },
          render: function (h: any) {
            return h(WhiteboardAttachment, {
              props: {
                postId: this.postId,
                uploadKey: this.uploadKey,
                width: this.width,
                height: this.height,
                zoom: this.zoom,
                xAudioPlayerV2: this.xAudioPlayerV2,
                shapeId: this.shapeId,
              },
              on: {
                loaded: this.handleOnLoaded,
                errored: this.handleOnErrored,
              },
            })
          },
          methods: {
            handleOnLoaded: function () {
              useWhiteboardAttachmentsStore().addPostCidToLoadedAttachments(this.postId)
            },
            handleOnErrored: function () {
              useWhiteboardAttachmentsStore().addPostCidToErroredAttachments(this.postId)
            },
          },
        },
      })
      this.vueApp[shape.id] = app as Vue
    }
    useEffect(() => {
      void createVueApp()
      return () => {
        this.vueApp[shape.id]?.$destroy()
      }
    }, [])

    // Update the Vue component when the shape props change
    useQuickReactor('update vue component zoom', () => {
      const editorZoom = this.editor.getZoomLevel()
      if (this.vueApp[shape.id] == null) return
      this.vueApp[shape.id].$data.zoom = editorZoom
    })

    useEffect(() => {
      const editorZoom = this.editor.getZoomLevel()
      const width = shape.props.w
      const height = shape.props.h
      if (this.vueApp[shape.id] == null) return
      this.vueApp[shape.id].$data.postId = shape.props.postId
      this.vueApp[shape.id].$data.width = width
      this.vueApp[shape.id].$data.height = height
      this.vueApp[shape.id].$data.zoom = editorZoom
    }, [shape.props.postId, shape.props.w, shape.props.h])

    const zoomCssStyle = useMemo(() => {
      // Special treatment for Safari, which renders the `zoom` properly weirdly.
      // `transform: scale()` is visually similar but DOM APIs like `getBoundingClientRect`
      // doesn't return the scaled size.
      return device.safari ? { transform: `scale(${shape.props.zoom})` } : { zoom: shape.props.zoom }
    }, [shape.props.zoom])

    return (
      <HTMLContainer id={shape.id} style={zoomCssStyle}>
        <div id={getVueContainerId(shape.props)}></div>
      </HTMLContainer>
    )
  }

  indicator(shape: PadletAttachmentShape): JSX.Element | null {
    const bounds = this.getGeometry(shape).bounds
    return <rect width={toDomPrecision(bounds.width)} height={toDomPrecision(bounds.height)} />
  }
}

export default PadletAttachmentShapeUtil
